export interface AgentErrorInfo {
  title: string;
  detail: string;
  hint: string;
  severity: 'quota' | 'auth' | 'model' | 'network' | 'parse' | 'unknown';
}

export function classifyAgentError(raw: string | null | undefined): AgentErrorInfo | null {
  if (!raw) return null;
  const s = raw.toLowerCase();

  // Strip the LLM_CONNECTION_ERROR: prefix for cleaner display
  const detail = raw.replace(/^LLM_CONNECTION_ERROR:\s*/i, '').trim();

  if (s.includes('throttlingexception') || s.includes('too many tokens') || s.includes('too many requests')) {
    return {
      severity: 'quota',
      title: 'Model quota exceeded',
      detail,
      hint: 'This Bedrock model has hit its daily token limit. Switch the agent to a Haiku model or wait until the quota resets (usually midnight UTC).',
    };
  }

  if (s.includes('accessdeniedexception') || s.includes('unable to locate credentials') || s.includes('nocredentialserror') || s.includes('expiredtokenexception') || s.includes('invalidclienttokenerror')) {
    return {
      severity: 'auth',
      title: 'AWS credentials error',
      detail,
      hint: 'Bedrock credentials are missing or expired. Update your AWS keys in LLM Provider settings.',
    };
  }

  if (s.includes('validationexception') || s.includes('resourcenotfoundexception') || s.includes("' not found") || s.includes('" not found') || s.includes('model not found')) {
    return {
      severity: 'model',
      title: 'Model not available',
      detail,
      hint: 'This model ID is not available in your AWS region or Ollama instance. Check the model name in agent settings.',
    };
  }

  if (s.includes('modelnotreadyexception') || s.includes('serviceunavailableexception')) {
    return {
      severity: 'network',
      title: 'Model temporarily unavailable',
      detail,
      hint: 'The model endpoint is not ready. This is usually transient — the agent will retry on the next cycle.',
    };
  }

  if (s.includes('connection refused') || s.includes('connect error') || s.includes('no such host') || s.includes('name or service not known') || s.includes('failed to connect')) {
    return {
      severity: 'network',
      title: 'Cannot reach LLM',
      detail,
      hint: 'The LLM service is unreachable. Check your Ollama container is running or your Bedrock endpoint is accessible.',
    };
  }

  if (s.includes('failed to parse') || s.includes('json')) {
    return {
      severity: 'parse',
      title: 'Invalid model response',
      detail,
      hint: 'The model returned output that could not be parsed as a valid signal. This can happen with smaller models — try a more capable model.',
    };
  }

  return {
    severity: 'unknown',
    title: 'Agent error',
    detail,
    hint: '',
  };
}
